import { Line } from '@cgs/data-access';
import {
  Container,
  EmptyAction,
  FunctionAction,
  IntervalAction,
  ParallelAction,
  ParamEvent,
  SceneObject,
  SequenceAction,
} from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { ISlotGameEngineProvider } from '../../../reels_engine/game_components_providers/i_slot_game_engine_provider';
import { ISlotGameEngine } from '../../../reels_engine/i_slot_game_engine';
import { IconsSoundModel } from '../../../reels_engine/icons_sound_model';
import { ReelsSoundModel } from '../../../reels_engine/reels_sound_model';
import {
  T_BaseSlotSoundController,
  T_IconsSoundModelComponent,
  T_ISlotGameEngineProvider,
  T_RegularSpinsSoundModelComponent,
  RESOURCES_COMPONENT_TOKEN,
  T_WinMovieSceneProvider,
} from '../../../type_definitions';
import { BaseSlotSoundController } from '../../common/base_slot_sound_controller';
import { FAST_SPINS_COMPONENT, FastSpinsComponent } from '../fast-spins.component';
import { IconsSoundModelComponent } from '../icons_sound_model_component';
import { RegularSpinsSoundModelComponent } from '../regular_spins_sound_model_component';
import { ResourcesComponent } from '../resources_component';
import { WinMovieSceneProvider } from '../win_movie_scene_provider';
import { IWinLineActionProvider } from './i_win_line_action_provider';

const DEFAULT_ANIM_NAME = 'anim';

export abstract class BaseWinLineActionProvider implements IWinLineActionProvider {
  public winLineAnimationAction: IntervalAction | null;
  public winLineStopAnimationAction: IntervalAction | null;
  public winLineRegularSoundAction: IntervalAction | null;
  public winLineWildSoundAction: IntervalAction | null;
  public winLineStopSoundAction: IntervalAction | null;
  public winMovieSceneAction: IntervalAction | null;
  public stopWinMovieSceneAction: IntervalAction | null;

  public winLineSoundPriority: number;
  public animationDuration: number;
  public soundId: number;
  public container: Container;

  protected animStates: SceneObject;

  private readonly _winMovieSceneProvider: WinMovieSceneProvider | null;
  private readonly _rootScene: SceneObject;
  private readonly _gameEngine: ISlotGameEngine;
  private readonly _iconsSoundModel: IconsSoundModel;
  private readonly _reelSoundModel: ReelsSoundModel;
  private readonly _slotSoundController: BaseSlotSoundController;
  private readonly _iconsSoundPriority: Map<number, number> | null;
  private readonly _substituteIconSound: Map<number, number> | null;
  private readonly _playIconSoundsWithStateMachine: boolean;
  private readonly _pauseBackSoundOnWinLines: boolean;
  private readonly _pauseBackSoundIconsIdsList: number[] | null;

  constructor(
    container: Container,
    iconsSoundPriority: Map<number, number> | null,
    substituteIconSound: Map<number, number> | null,
    playIconSoundsWithStateMachine: boolean,
    pauseBackSoundOnWinLines: boolean,
    pauseBackSoundIconsIdsList: number[] | null
  ) {
    this.container = container;
    this._iconsSoundPriority = iconsSoundPriority;
    this._substituteIconSound = substituteIconSound;
    this._playIconSoundsWithStateMachine = playIconSoundsWithStateMachine;
    this._pauseBackSoundOnWinLines = pauseBackSoundOnWinLines;
    this._pauseBackSoundIconsIdsList = pauseBackSoundIconsIdsList;

    this._winMovieSceneProvider = this.container.resolve<WinMovieSceneProvider>(
      T_WinMovieSceneProvider,
      { optional: true }
    );
    this._rootScene = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).root;
    this._gameEngine =
      this.container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider).gameEngine;
    this._iconsSoundModel = this.container.resolve<IconsSoundModelComponent>(
      T_IconsSoundModelComponent
    ).iconsSoundModel;
    this._reelSoundModel = container.resolve<RegularSpinsSoundModelComponent>(
      T_RegularSpinsSoundModelComponent
    ).regularSpinSoundModel;
    this._slotSoundController =
      this.container.resolve<BaseSlotSoundController>(T_BaseSlotSoundController);
    this.animStates = this._rootScene.findById('statesAnim')!;
  }

  public createActions(winLine: Line, animName: string = DEFAULT_ANIM_NAME): void {
    this.winLineAnimationAction = null;
    this.winLineStopAnimationAction = null;
    this.winLineRegularSoundAction = null;
    this.winLineWildSoundAction = null;
    this.winLineStopSoundAction = null;
    this.winMovieSceneAction = null;
    this.stopWinMovieSceneAction = null;

    this.createAnimationActions(winLine, animName);
    this.createSoundActions(winLine);
    this.createWinMovieActions(winLine.winName, animName);
  }

  protected createWinMovieActions(winName: string, animName: string): void {
    if (this._winMovieSceneProvider && winName && winName.length > 0) {
      const winMovieScene = this._winMovieSceneProvider.getWinMovieScene(winName);

      if (winMovieScene) {
        const rootNode = winMovieScene.findById('root');
        if (rootNode) {
          const animDuration = (
            rootNode.stateMachine!.findById(animName)?.enterAction as IntervalAction
          ).duration;

          this.winMovieSceneAction = new SequenceAction([
            new FunctionAction(() => this._rootScene.addChild(winMovieScene)),
            new FunctionAction(() => rootNode.initialize()),
            new FunctionAction(() =>
              rootNode.stateMachine!.dispatchEvent(new ParamEvent(animName))
            ),
            new FunctionAction(() => this.pauseBackgroundSound([])),
            new EmptyAction().withDuration(animDuration),
          ]);

          this.stopWinMovieSceneAction = new SequenceAction([
            new FunctionAction(() =>
              rootNode.stateMachine!.dispatchEvent(new ParamEvent('default'))
            ),
            new FunctionAction(() => this.playBackgroundSound()),
            new FunctionAction(() => rootNode.deinitialize()),
            new FunctionAction(() => this._rootScene.removeChild(winMovieScene)),
          ]);
        }
      }
    }
  }

  public createSoundActions(winLine: Line): void {
    const fastSpinsController = this.container.resolve<FastSpinsComponent>(FAST_SPINS_COMPONENT);
    const gameStateMachine = this.container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
    if (
      fastSpinsController.isFastSpinsActive &&
      (gameStateMachine.isAutoSpins || gameStateMachine.curResponse.isFreeSpins) &&
      !this._iconsSoundModel.isFreeFallIconsSoundModel
    ) {
      return;
    }
    if (this._iconsSoundPriority) {
      this.createSoundActionsUsingCustomPriority(winLine);
    } else {
      this.winLineSoundPriority = -1;
      this.createSoundActionsUsingRegularPriority(winLine);
    }
  }

  public createSoundActionsUsingCustomPriority(winLine: Line): void {
    let highestPriority = -1;

    const drawIds: number[] = [];

    for (const position of winLine.positions) {
      const iconIds = this._gameEngine.getSoundIconIds(position);
      if (iconIds && iconIds.length > 0) {
        let drawId = iconIds[0];

        if (drawId > 100) {
          drawId = drawId - (drawId % 100);
        }

        drawIds.push(drawId);

        const soundPriorityId = this._iconsSoundPriority?.get(drawId) as number;
        if (
          this._iconsSoundModel.hasSound(drawId) &&
          this._iconsSoundPriority?.has(drawId) &&
          (highestPriority < 0 || (highestPriority >= 0 && soundPriorityId < highestPriority))
        ) {
          highestPriority = this._iconsSoundPriority.get(drawId) as number;
        }
      }
    }

    const soundIconIds: number[] = [];

    this._iconsSoundPriority?.forEach((value, key) => {
      if (drawIds.includes(key) && value === highestPriority) {
        soundIconIds.push(key);
      }
    });

    this.winLineSoundPriority = highestPriority;
    this.winLineRegularSoundAction = null;
    this.winLineWildSoundAction = null;
    this.winLineStopSoundAction = null;

    if (soundIconIds.length > 0) {
      const playSoundActions = soundIconIds.map(
        (id) => new FunctionAction(() => this.playIconSound(id), false)
      );
      playSoundActions.push(new FunctionAction(() => this.pauseBackgroundSound(soundIconIds)));
      this.winLineRegularSoundAction = new ParallelAction(playSoundActions);

      const stopSoundActions = soundIconIds.map(
        (id) => new FunctionAction(() => this.stopIconSound(id), false)
      );
      stopSoundActions.push(new FunctionAction(() => this.playBackgroundSound()));
      this.winLineStopSoundAction = new ParallelAction(stopSoundActions);
    }
  }

  public substituteSoundId(sourceId: number): number {
    return this._substituteIconSound && this._substituteIconSound.has(sourceId)
      ? (this._substituteIconSound.get(sourceId) as number)
      : sourceId;
  }

  public createSoundActionsUsingRegularPriority(winLine: Line): void {
    for (const position of winLine.positions) {
      const iconIds = this._gameEngine.getSoundIconIds(position);

      if (iconIds && iconIds.length > 0) {
        const drawId = winLine.symbolId!;

        if (this._iconsSoundModel.hasSound(drawId)) {
          const soundAction = new SequenceAction([
            new FunctionAction(() => this.pauseBackgroundSound([drawId])),
            new FunctionAction(() => this.playIconSound(drawId), false),
          ]);
          this.winLineStopSoundAction = new SequenceAction([
            new FunctionAction(() => this.playBackgroundSound()),
            new FunctionAction(() => this.stopIconSound(drawId), false),
          ]);

          this.soundId = drawId;

          this.winLineRegularSoundAction = soundAction;
          break;
        } else {
          const soundAction = new SequenceAction([
            new FunctionAction(() => this.pauseBackgroundSound([drawId])),
            new FunctionAction(() => this._reelSoundModel.winSound.play(), false),
          ]);

          this.winLineStopSoundAction = new SequenceAction([
            new FunctionAction(() => this.playBackgroundSound()),
            new FunctionAction(() => this._reelSoundModel.winSound.stop(), false),
          ]);

          this.soundId = drawId;

          this.winLineRegularSoundAction = soundAction;
        }
      }
    }
  }

  protected abstract createAnimationActions(winLine: Line, animName: string): void;

  public playIconSound(iconId: number): void {
    iconId = this.substituteSoundId(iconId);
    if (this._playIconSoundsWithStateMachine) {
      const soundNodes = this._iconsSoundModel.getIconSoundNodes(iconId);
      if (soundNodes && soundNodes.length > 0) {
        const node = soundNodes.find((n) => n.stateMachine && n.stateMachine.isActive('default'));

        if (node) {
          node.stateMachine!.switchToState('sound');
        }
      }
    } else {
      this._iconsSoundModel.getIconSound(iconId).stop();
      this._iconsSoundModel.getIconSound(iconId).play();
    }
  }

  public stopIconSound(iconId: number): void {
    if (!this._playIconSoundsWithStateMachine) {
      iconId = this.substituteSoundId(iconId);
      this._iconsSoundModel.getIconSound(iconId).stop();
    }
  }

  public pauseBackgroundSound(drawIds: number[]): void {
    if (
      this._pauseBackSoundOnWinLines &&
      this._slotSoundController &&
      (!this._pauseBackSoundIconsIdsList ||
        !drawIds ||
        this._pauseBackSoundIconsIdsList.some((x) => drawIds.includes(x)))
    ) {
      this._slotSoundController.pauseBackgroundSound();
    }
  }

  public playBackgroundSound(): void {
    if (this._pauseBackSoundOnWinLines && this._slotSoundController) {
      this._slotSoundController.playBackgroundSound();
    }
  }
}
